import ILoginResponse from "../types/services/ILoginResponse";
import ISignupResponse from "../types/services/ISignupResponse";
import IPaginatedResponse from "../types/services/IPaginatedResponse";
import IUser from "../types/services/IUser";
import IDonation from "../types/services/IDonation";
import moment from "moment-timezone";
import { PostType } from "../types/services/IPost";
import { moneyFormat, dateFormat, normalizeAudienceMetrics } from "./helpers";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import IExtendedUser from "../types/services/IExtendedUser";
import ICreationRequest, {
  IUpdateRequest,
} from "../types/services/IUserManagementRequests";
import ICheckAdminResponse from "../types/services/ICheckAdminResponse";
import ISavePasswordRequest from "../types/services/ISavePasswordRequest";
import { IChatListResponse } from "../types/services/IChatListResponse";
import { IChat } from "../types/IMessage";
import {
  ISendMessageRequest,
  ISendMessageResponse,
} from "../types/services/ISendMessage";
import IBasicResponse from "../types/services/IBasicResponse";
import {
  IAudienceMetrics,
  ISparkPostMetrics,
} from "../types/services/IMetrics";
import IValidtimeResponse from "../types/services/IValidtimeResponse";
import ITitleLength from "../types/services/ITitleLengthResponse";
import IContentResponse from "../types/services/IContentResponse";
import ISetFlagsForDonorRequest from "../types/services/ISetFlagsForDonorRequest";
import ISegment from "../types/services/ISegment";
import { BrandAssetsResponse } from "../Admin/components/charitySetup/onboardingSteps/brandSetup/types";
import { ITargetCount } from "../types/ITargetCount";
import IReindexActiveStatus from "../types/services/IReindexActiveStatus";
import IAdminMetrics from "../types/services/IAdminMetrics";
import IClonePostResponse from "../types/services/IClonePostResponse";
import { IPhoneRevalidationResult } from "../types/services/IPhoneRevalidation";
import IEmailAccount from "../types/IEmailAccount";
import {
  IDonorPerfectConfig,
  IFlagsResponse as ICodesResponse,
  IConnectResponse,
  ISyncingRequest,
  ISyncResult,
} from "../types/services/DonorPerfectTypes";
import { Segment } from "../Admin/components/integrations/common/segmentsPills/types";
import {
  ensureAccessToken,
  isUsableServiceAccessToken,
  resolveFirebaseIdTokenForApiCall,
} from "./firebaseToken";
import IAdminAuthLoginResult from "../types/services/IAdminAuthLoginResult";
import {
  IAdminOtpChallengeResponse,
  IAdminOtpVerifyResponse,
} from "../types/services/IAdminOtp";
import {
  notifySessionExpiredFromBody,
  payloadIndicatesSessionExpired,
} from "./sessionExpiryEvents";

export type DonorActivationFilterParam =
  | "ALL"
  | "ACTIVE_ONLY"
  | "DEACTIVATED_ONLY";

export interface FetchData {
  url: string;
  method: string;
  mode: RequestMode;
  headers?: any;
  body?: any;
  suppressSessionExpiredNotification?: boolean;
}

/** Sends staff credentials in headers while preserving public invitation link tokens. */
export function staffFetch(
  url: string,
  init: RequestInit = {}
): Promise<Response> {
  const parsed = new URL(url);
  const headers = new Headers(init.headers);
  const token = parsed.searchParams.get("token");
  if (token && !parsed.pathname.endsWith("/charity/invite/check")) {
    parsed.searchParams.delete("token");
    if (!headers.has("Authorization"))
      headers.set("Authorization", `Bearer ${token}`);
  }
  return fetch(parsed.toString(), { ...init, headers, redirect: "error" });
}

async function readJsonOrEmpty(
  res: Response
): Promise<{ body: any; text: string }> {
  const text = await res.text();
  if (!text) {
    return { body: {}, text: "" };
  }
  try {
    return { body: JSON.parse(text), text };
  } catch {
    return { body: {}, text };
  }
}

function unwrapAdminAuthPayload(raw: any): any {
  if (!raw || typeof raw !== "object") {
    return raw;
  }
  const inner =
    (raw.data && typeof raw.data === "object" && raw.data) ||
    (raw.result && typeof raw.result === "object" && raw.result) ||
    (raw.body && typeof raw.body === "object" && raw.body) ||
    (raw.value && typeof raw.value === "object" && raw.value) ||
    null;
  if (
    inner &&
    (inner.stage != null ||
      inner.token != null ||
      inner.challengeId != null ||
      inner.challenge_id != null)
  ) {
    return inner;
  }
  return raw;
}

function normalizeAdminAuthLoginResult(raw: any): IAdminAuthLoginResult {
  const b = unwrapAdminAuthPayload(raw);
  if (!b || typeof b !== "object") {
    throw new Error("Unexpected login response.");
  }
  const stageFromServer = b.stage ?? b.STAGE;
  const stage =
    typeof stageFromServer === "string"
      ? stageFromServer.trim().toUpperCase()
      : undefined;

  const loginNested = b.loginResponse ?? b.login_response ?? undefined;
  const challengeId = b.challengeId ?? b.challenge_id;
  const expiresRaw = b.expiresInSeconds ?? b.expires_in_seconds;
  const expiresInSeconds =
    typeof expiresRaw === "number" ? expiresRaw : undefined;

  const flatLogin =
    b.token != null && b.user != null
      ? {
          token: b.token as string,
          user: b.user,
          message: (b.message != null ? String(b.message) : "") as string,
        }
      : undefined;

  const loginResponse =
    loginNested && loginNested.token != null
      ? {
          token: loginNested.token as string,
          user: loginNested.user,
          message: (loginNested.message != null
            ? String(loginNested.message)
            : "") as string,
        }
      : flatLogin;

  if (stage === "TOKEN" && loginResponse) {
    return {
      stage: "TOKEN",
      loginResponse,
      challengeId: undefined,
      expiresInSeconds: undefined,
    };
  }
  const smsEligibleRaw = b.smsEligible ?? b.sms_eligible;
  const smsEligible = smsEligibleRaw === true;
  const deliveryChannelRaw = b.deliveryChannel ?? b.delivery_channel;
  const deliveryChannel =
    typeof deliveryChannelRaw === "string"
      ? deliveryChannelRaw.trim().toLowerCase() === "sms"
        ? "sms"
        : "email"
      : undefined;

  if (stage === "CHALLENGE" && challengeId) {
    return {
      stage: "CHALLENGE",
      challengeId: String(challengeId),
      expiresInSeconds,
      loginResponse: undefined,
      smsEligible,
      deliveryChannel,
    };
  }

  if (!stage && loginResponse) {
    return {
      stage: "TOKEN",
      loginResponse,
      challengeId: undefined,
      expiresInSeconds: undefined,
    };
  }
  if (!stage && challengeId) {
    return {
      stage: "CHALLENGE",
      challengeId: String(challengeId),
      expiresInSeconds,
      loginResponse: undefined,
      smsEligible,
      deliveryChannel,
    };
  }

  throw new Error("Unexpected login response.");
}

export async function executeAdminLogin(
  authUrl: string,
  data: { email: string; password: string }
): Promise<IAdminAuthLoginResult> {
  const fullUrl = `${authUrl}/v1/admin/login`;
  const res = await staffFetch(fullUrl, {
    method: "POST",
    mode: "cors",
    body: JSON.stringify({ ...data, clientType: "web" }),
    headers: {
      "content-type": "application/json",
    },
  });
  const { body, text } = await readJsonOrEmpty(res);
  if (!res.ok) {
    notifySessionExpiredFromBody(body, text);
    throw new Error(body.message || text || "Login failed");
  }
  return normalizeAdminAuthLoginResult(body);
}

export async function executeAdminLoginComplete(
  authUrl: string,
  completionToken: string
): Promise<IAdminAuthLoginResult> {
  const fullUrl = `${authUrl}/v1/admin/login-complete`;
  const res = await staffFetch(fullUrl, {
    method: "POST",
    mode: "cors",
    body: JSON.stringify({ completionToken }),
    headers: {
      "content-type": "application/json",
    },
  });
  const { body, text } = await readJsonOrEmpty(res);
  if (!res.ok) {
    notifySessionExpiredFromBody(body, text);
    throw new Error(body.message || text || "Login failed");
  }
  return normalizeAdminAuthLoginResult(body);
}

export async function adminOtpChallenge(
  authUrl: string,
  requestBody: {
    clientType: string;
    operation: string;
    operationId?: string;
    firebaseToken?: string;
    newPhoneNumber?: string;
  }
): Promise<IAdminOtpChallengeResponse> {
  const fullUrl = `${authUrl}/v1/admin/challenge`;
  const res = await staffFetch(fullUrl, {
    method: "POST",
    mode: "cors",
    body: JSON.stringify(requestBody),
    headers: {
      "content-type": "application/json",
    },
  });
  const { body, text } = await readJsonOrEmpty(res);
  if (!res.ok) {
    notifySessionExpiredFromBody(body, text);
    throw new Error(body.message || text || "Challenge failed");
  }
  const deliveryChannelRaw = body.deliveryChannel ?? body.delivery_channel;
  const deliveryChannel =
    typeof deliveryChannelRaw === "string"
      ? deliveryChannelRaw.trim().toLowerCase() === "sms"
        ? "sms"
        : "email"
      : undefined;
  return {
    ...(body as IAdminOtpChallengeResponse),
    deliveryChannel,
  };
}

export async function adminOtpVerify(
  authUrl: string,
  challengeId: string,
  code: string
): Promise<IAdminOtpVerifyResponse> {
  const fullUrl = `${authUrl}/v1/admin/verify`;
  const res = await staffFetch(fullUrl, {
    method: "POST",
    mode: "cors",
    body: JSON.stringify({ challengeId, code }),
    headers: {
      "content-type": "application/json",
    },
  });
  const { body, text } = await readJsonOrEmpty(res);
  if (!res.ok) {
    notifySessionExpiredFromBody(body, text);
    throw new Error(body.message || text || "Verification failed");
  }
  return body as IAdminOtpVerifyResponse;
}

export async function adminOtpResend(
  authUrl: string,
  challengeId: string,
  deliveryChannel?: "email" | "sms"
): Promise<string> {
  const fullUrl = `${authUrl}/v1/admin/resend`;
  const payload: { challengeId: string; deliveryChannel?: string } = {
    challengeId,
  };
  if (deliveryChannel) {
    payload.deliveryChannel = deliveryChannel;
  }
  const res = await staffFetch(fullUrl, {
    method: "POST",
    mode: "cors",
    body: JSON.stringify(payload),
    headers: {
      "content-type": "application/json",
    },
  });
  const { body, text } = await readJsonOrEmpty(res);
  if (!res.ok) {
    notifySessionExpiredFromBody(body, text);
    throw new Error(body.message || text || "Resend failed");
  }
  return body.message || text || "";
}

export async function confirmAdminPhone(
  authUrl: string,
  completionToken: string
): Promise<string> {
  const fullUrl = `${authUrl}/v1/admin/confirmPhone`;
  const res = await staffFetch(fullUrl, {
    method: "POST",
    mode: "cors",
    body: JSON.stringify({ completionToken }),
    headers: {
      "content-type": "application/json",
    },
  });
  const { body, text } = await readJsonOrEmpty(res);
  if (!res.ok) {
    notifySessionExpiredFromBody(body, text);
    throw new Error(body.message || text || "Confirmation failed");
  }
  return body.message || text || "";
}

export function executeForgotPassword(
  authUrl: string,
  data: any
): Promise<string> {
  const fullUrl = `${authUrl}/v1/admin/forgot`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.message);
}

export function checkSignupInvitation(
  authUrl: string,
  token: string
): Promise<{ valid: boolean; email: string | null }> {
  const fullUrl = `${authUrl}/v1/charity/invite/check?token=${encodeURIComponent(
    token
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  });
}

export function executeSignup(
  authUrl: string,
  data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    invitationToken: string;
  }
): Promise<ISignupResponse> {
  const fullUrl = `${authUrl}/v1/charity/signup`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    if (response.message && !response.success) {
      throw new Error(response.message);
    } else {
      return response;
    }
  });
}

export function executeVerifyEmail(
  authUrl: string,
  verificationId: string
): Promise<ISignupResponse> {
  const fullUrl = `${authUrl}/v1/charity/verify?verificationId=${encodeURIComponent(
    verificationId
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    if (response.message && !response.success) {
      throw new Error(response.message);
    } else {
      return response;
    }
  });
}

export function executeResendVerification(
  authUrl: string,
  email: string
): Promise<ISignupResponse> {
  const fullUrl = `${authUrl}/v1/charity/resend-verification?email=${encodeURIComponent(
    email
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    if (response.message && !response.success) {
      throw new Error(response.message);
    } else {
      return response;
    }
  });
}

export async function getVideoUrl(
  adminUrl: string,
  charityId: string,
  videoId: string,
  token: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/videos/${videoId}?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => response.link);
}

export async function setNewApiKey(
  adminUrl: string,
  charityId: string,
  apiKey: string,
  token: string
): Promise<boolean> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/sms/set/apiKey?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: apiKey,
  }).then(response => response);
}

export async function getCharityProvider(
  adminUrl: string,
  charityId: string,
  token: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/sms/get/provider?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => response.message);
}

export async function paginateDonors(
  searchUrl: string,
  charityId: string,
  pageNumber: number,
  pageSize: number,
  sortBy: string,
  asc: boolean,
  token: string,
  filter: string,
  from: number,
  to: number,
  ignoreDeactivated: boolean = false,
  activationFilter?: DonorActivationFilterParam
): Promise<IPaginatedResponse<IUser>> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const activationQs =
    activationFilter != null
      ? `&activationFilter=${encodeURIComponent(activationFilter)}`
      : "";

  const fullUrl =
    `${searchUrl}/api/v1/donors/paginated/${charityId}?token=${encodeURIComponent(
      resolved
    )}` +
    `&pageNumber=${encodeURIComponent(pageNumber.toString())}` +
    `&pageSize=${encodeURIComponent(pageSize.toString())}` +
    `&sortBy=${encodeURIComponent(sortBy)}` +
    `&ignoreDeactivated=${ignoreDeactivated}` +
    `&ascending=${encodeURIComponent(asc ? "true" : "false")}` +
    (filter ? `&filterBy=${encodeURIComponent(filter)}` : "") +
    `${from ? "&from=" + encodeURIComponent(from.toString()) : ""}` +
    `${to ? "&to=" + encodeURIComponent(to.toString()) : ""}` +
    activationQs;
  const time = new Date().getTime();
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => {
    response.result = response.result.map(processDonor);
    return {
      time: time,
      ...response,
    };
  });
}

export async function searchDonors(
  searchUrl: string,
  charityId: string,
  pageSize: number,
  page: number,
  query: string,
  token: string,
  sortBy: string,
  asc: boolean,
  filter: string,
  from: number,
  to: number,
  expanded: boolean = true,
  ignoreDeactivated: boolean = false,
  activationFilter?: DonorActivationFilterParam
): Promise<IPaginatedResponse<IUser>> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const activationQs =
    activationFilter != null
      ? `&activationFilter=${encodeURIComponent(activationFilter)}`
      : "";

  const fullUrl =
    `${searchUrl}/api/v1/donors?expanded=${expanded}&charityId=${encodeURIComponent(
      charityId
    )}&query=${encodeURIComponent(query)}
  &token=${encodeURIComponent(resolved)}
  &sortBy=${encodeURIComponent(sortBy)}
  &ascending=${encodeURIComponent(asc ? "true" : "false")}
  &ignoreDeactivated=${ignoreDeactivated}` +
    (filter ? `&filterBy=${encodeURIComponent(filter)}` : "") +
    `${from ? "&from=" + encodeURIComponent(from.toString()) : ""}` +
    `${to ? "&to=" + encodeURIComponent(to.toString()) : ""}` +
    `&pageNumber=${encodeURIComponent(page.toString())}` +
    `&pageSize=${encodeURIComponent(pageSize.toString())}` +
    activationQs;
  const time = new Date().getTime();
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => {
    response.result = response.result.map(processDonor);
    return {
      time: time,
      ...response,
    };
  });
}

export async function searchDonorsByIds(
  searchUrl: string,
  charityId: string,
  ids: Array<string>,
  token: string
): Promise<Array<IUser>> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/${charityId}/donor/lookup?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(ids),
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response);
}

export async function getPostEmailMetrics(
  searchUrl: string,
  charityId: string,
  postId: string,
  token: string
): Promise<ISparkPostMetrics> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/metrics/email/${charityId}/${postId}?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response);
}

export async function getPostAudienceMetrics(
  searchUrl: string,
  charityId: string,
  postId: string,
  token: string
): Promise<IAudienceMetrics> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/metrics/audience/${charityId}/${postId}?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => normalizeAudienceMetrics(response));
}

export async function populatePostAudienceMetrics(
  searchUrl: string,
  charityId: string,
  postId: string,
  token: string
): Promise<IAudienceMetrics> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/metrics/audience/${charityId}/${postId}?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => normalizeAudienceMetrics(response));
}

export async function getPostSmsMetrics(
  searchUrl: string,
  charityId: string,
  postId: string,
  token: string,
  shortUrl: string
): Promise<{ [key: string]: number }> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/metrics/sms/${charityId}/${postId}?token=${encodeURIComponent(
    resolved
  )}&shortUrl=${shortUrl}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response);
}

export async function getPostSmsUnsubscribedMetrics(
  searchUrl: string,
  charityId: string,
  postId: string,
  token: string,
  filterBy: string
): Promise<{ [key: string]: number }> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/metrics/${charityId}/${postId}/unsubscribed?token=${encodeURIComponent(
    resolved
  )}&filterBy=${filterBy}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response);
}

export async function searchFlagById(
  searchUrl: string,
  charityId: string,
  flagId: string,
  token: string,
  query: string
): Promise<Array<IUser>> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl = `${searchUrl}/api/v1/donors/lookup/flag?token=${encodeURIComponent(
    resolved
  )}&flagId=${encodeURIComponent(flagId)}&charityId=${encodeURIComponent(
    charityId
  )}`;
  if (query) {
    fullUrl += `&query=${encodeURIComponent(query)}`;
  }
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    response.result = response.result.map(processDonor);
    return response.result;
  });
}

export async function searchDonorById(
  adminUrl: string,
  charityId: string,
  id: string,
  token: string
): Promise<IUser> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/donor/lookup?token=${encodeURIComponent(
    resolved
  )}&id=${encodeURIComponent(id)}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    if (!response.notifications) {
      response.notifications = {};
      response.notifications.push = false;
      response.notifications.sms = true;
      response.notifications.email = true;
    }
    return response;
  });
}

export async function searchDonorsByIdsExtended(
  searchUrl: string,
  charityId: string,
  ids: Array<string>,
  token: string
): Promise<Array<IUser>> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/donors/lookup?charityId=${encodeURIComponent(
    charityId
  )}&token=${encodeURIComponent(resolved)}`;

  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(ids),
  }).then(response => {
    response.result = response.result.map(processDonor);
    return response.result;
  });
}

export async function searchDonorByIdExtended(
  searchUrl: string,
  charityId: string,
  donorId: string,
  token: string
): Promise<IUser> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/donor/lookup?charityId=${encodeURIComponent(
    charityId
  )}&token=${encodeURIComponent(resolved)}&donorId=${encodeURIComponent(
    donorId
  )}`;

  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  });
}

export async function paginateUsers(
  searchUrl: string,
  charityId: string,
  pageNumber: number,
  pageSize: number,
  sortBy: string,
  asc: boolean,
  token: string
): Promise<IPaginatedResponse<IExtendedUser>> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl =
    `${searchUrl}/api/v1/admins/paginated/${charityId}?token=${encodeURIComponent(
      resolved
    )}` +
    `&pageNumber=${encodeURIComponent(pageNumber.toString())}` +
    `&pageSize=${encodeURIComponent(pageSize.toString())}` +
    `&sortBy=${encodeURIComponent(sortBy)}
     &ascending=${encodeURIComponent(asc ? "true" : "false")}`;
  const time = new Date().getTime();
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => {
    response.result = response.result.map(user => {
      const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      if (user.lastLogin) {
        user.lastLoginPretty = new Date(user.lastLogin).toLocaleDateString(
          "en-US",
          options
        );
      }
      if (user.createdOn) {
        user.createdOnPretty = new Date(user.createdOn).toLocaleDateString(
          "en-US",
          options
        );
      }
      if (user.role) {
        if (user.role === "COLLABORATOR") {
          user.rolePretty = "Contributor";
        } else if (user.role === "READER") {
          user.rolePretty = "Read-Only";
        } else {
          user.rolePretty =
            user.role.substr(0, 1).toUpperCase() +
            user.role.substr(1).toLowerCase();
        }
      }
      return user;
    });
    return {
      time: time,
      ...response,
    };
  });
}

export async function searchUsersById(
  searchUrl: string,
  charityId: string,
  ids: Array<string>,
  token: string
): Promise<IExtendedUser> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/admins/lookup?charityId=${encodeURIComponent(
    charityId
  )}&token=${encodeURIComponent(resolved)}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(ids),
  }).then(response => {
    if (response.result.length > 0) {
      let user = response.result[0];
      if (user.role) {
        if (user.role === "COLLABORATOR") {
          user.rolePretty = "Contributor";
        } else {
          user.rolePretty =
            user.role.substr(0, 1).toUpperCase() +
            user.role.substr(1).toLowerCase();
        }
      }
      return user;
    }
    return {};
  });
}

export async function searchUserByEmail(
  authUrl: string,
  charityId: string,
  email: string,
  token: string
): Promise<IExtendedUser> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${authUrl}/v1/admin/lookup/email?token=${encodeURIComponent(
    resolved
  )}&realm=${encodeURIComponent(charityId)}&email=${encodeURIComponent(email)}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    if (response && response.role) {
      response.rolePretty =
        response.role.substr(0, 1).toUpperCase() +
        response.role.substr(1).toLowerCase();
    }
    return response;
  });
}

export async function createUser(
  authUrl: string,
  token: string,
  data: ICreationRequest
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${authUrl}/v1/admin/create?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.message);
}

export async function updateUser(
  authUrl: string,
  token: string,
  data: IUpdateRequest
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${authUrl}/v1/admin/update?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.message);
}

export async function deleteUser(
  authUrl: string,
  token: string,
  userId: string,
  charityId: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${authUrl}/v1/admin/delete?token=${encodeURIComponent(
    resolved
  )}&userId=${encodeURIComponent(userId)}&realm=${encodeURIComponent(
    charityId
  )}`;
  return customFetch({
    url: fullUrl,
    method: "DELETE",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.message);
}

export async function searchUsers(
  searchUrl: string,
  charityId: string,
  query: string,
  token: string,
  sortBy: string,
  asc: boolean
): Promise<Array<IExtendedUser>> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/admins?charityId=${encodeURIComponent(
    charityId
  )}&query=${encodeURIComponent(query)}
  &token=${encodeURIComponent(resolved)}
  &sortBy=${encodeURIComponent(sortBy)}
  &ascending=${encodeURIComponent(asc ? "true" : "false")}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => {
    response.result = response.result.map(user => {
      if (user.lastLogin) {
        user.lastLoginPretty = new Date(user.lastLogin).toLocaleDateString(
          "en-US",
          {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          }
        );
      }
      if (user.createdOn) {
        user.createdOnPretty = new Date(user.createdOn).toLocaleDateString(
          "en-US",
          {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }
        );
      }
      if (user.role) {
        user.rolePretty =
          user.role.substr(0, 1).toUpperCase() +
          user.role.substr(1).toLowerCase();
      }
      return user;
    });
    return response.result;
  });
}

export function checkAdminInvite(
  authUrl: string,
  operationId: string
): Promise<ICheckAdminResponse> {
  const fullUrl = `${authUrl}/v1/admin/checkInvite?operationId=${encodeURIComponent(
    operationId
  )}`;
  return basicCustomFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    if (response.status === 200) {
      return response.json();
    } else {
      throw response.statusText;
    }
  });
}

export function checkForgotPasswordOperation(
  authUrl: string,
  operationId: string
): Promise<ICheckAdminResponse> {
  const fullUrl = `${authUrl}/v1/admin/checkForgotPassword?operationId=${encodeURIComponent(
    operationId
  )}`;
  return basicCustomFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    if (response.status === 200) {
      return response.json();
    } else {
      throw response.statusText;
    }
  });
}

export function resendAdminInvite(
  authUrl: string,
  token: string,
  userId: string,
  realm: string
): Promise<string> {
  const fullUrl = `${authUrl}/v1/admin/invite?token=${encodeURIComponent(
    token
  )}&userId=${encodeURIComponent(userId)}&realm=${encodeURIComponent(realm)}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.message);
}

export function saveNewAdminPassword(
  authUrl: string,
  data: ISavePasswordRequest
): Promise<string> {
  const fullUrl = `${authUrl}/v1/admin/acceptInvite`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.message);
}

export function saveForgotPassword(
  authUrl: string,
  data: ISavePasswordRequest
): Promise<string> {
  const fullUrl = `${authUrl}/v1/admin/acceptForgotPassword`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.message);
}

export async function executeUsersExport(
  searchUrl: string,
  charityId: string,
  targetsId: Array<string>,
  ignoreSelected: boolean,
  token: string
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl = `${searchUrl}/api/v1/admins/export/${charityId}?token=${encodeURIComponent(
    resolved
  )}&ignoreSelected=${ignoreSelected !== undefined ? ignoreSelected : false}`;
  const tz = moment.tz.guess();
  if (tz) {
    fullUrl += `&timezone=${encodeURIComponent(tz)}`;
  }
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(targetsId),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function getSegmentDonors(
  searchUrl: string,
  charityId: string,
  segmentId: string,
  token: string
): Promise<IPaginatedResponse<IUser>> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/${charityId}/segments/${segmentId}?token=${encodeURIComponent(
    resolved
  )}`;
  const time = new Date().getTime();
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    return {
      result: response,
      totalAmount: response.length,
      time: time,
    };
  });
}

export async function paginateDonations(
  searchUrl: string,
  charityId: string,
  pageNumber: number,
  pageSize: number,
  sortBy: string,
  asc: boolean,
  from: number,
  to: number,
  donorId: string,
  token: string
): Promise<IPaginatedResponse<IDonation>> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl =
    `${searchUrl}/api/v1/donations/paginated/${charityId}?token=${encodeURIComponent(
      resolved
    )}` +
    `&pageNumber=${encodeURIComponent(pageNumber.toString())}` +
    `&pageSize=${encodeURIComponent(pageSize.toString())}` +
    `&sortBy=${encodeURIComponent(sortBy)}` +
    `&ascending=${encodeURIComponent(asc ? "true" : "false")}` +
    `${from ? "&from=" + encodeURIComponent(from.toString()) : ""}` +
    `${to ? "&to=" + encodeURIComponent(to.toString()) : ""}` +
    `${donorId ? "&donorId=" + encodeURIComponent(donorId) : ""}`;
  const time = new Date().getTime();
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(r => {
    const result = r as IPaginatedResponse<IDonation>;
    result.time = time;
    result.result = result.result.map(processDonation);
    return result;
  });
}

export async function getAdminMetrics(
  searchUrl: string,
  charityId: string,
  from: number,
  to: number,
  token: string
): Promise<IAdminMetrics> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl =
    `${searchUrl}/api/v1/metrics/admin?token=${encodeURIComponent(resolved)}` +
    `&charityId=${encodeURIComponent(charityId)}` +
    `${from ? "&from=" + encodeURIComponent(from.toString()) : ""}` +
    `${to ? "&to=" + encodeURIComponent(to.toString()) : ""}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function getSuperadminMetrics(
  searchUrl: string,
  charityIds: Array<string>,
  from: number,
  to: number,
  token: string
): Promise<IAdminMetrics> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl =
    `${searchUrl}/api/v1/metrics/superadmin?token=${encodeURIComponent(
      resolved
    )}` +
    `${from ? "&from=" + encodeURIComponent(from.toString()) : ""}` +
    `${to ? "&to=" + encodeURIComponent(to.toString()) : ""}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    body: JSON.stringify(charityIds),
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function executeSuperadminMetricsAggregationExport(
  searchUrl: string,
  charityIds: Array<string>,
  from: number,
  to: number,
  token: string
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl =
    `${searchUrl}/api/v1/export/metrics/superadmin?token=${encodeURIComponent(
      resolved
    )}` +
    `${from ? "&from=" + encodeURIComponent(from.toString()) : ""}` +
    `${to ? "&to=" + encodeURIComponent(to.toString()) : ""}`;
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    body: JSON.stringify(charityIds),
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function executeAdminMetricsAggregationExport(
  searchUrl: string,
  charityId: string,
  from: number,
  to: number,
  token: string
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl =
    `${searchUrl}/api/v1/export/metrics/admin?token=${encodeURIComponent(
      resolved
    )}` +
    `&charityId=${encodeURIComponent(charityId)}` +
    `${from ? "&from=" + encodeURIComponent(from.toString()) : ""}` +
    `${to ? "&to=" + encodeURIComponent(to.toString()) : ""}`;
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function searchDonationById(
  adminUrl: string,
  charityId: string,
  donationId: string,
  token: string
): Promise<IDonation> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/${donationId}?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => processDonation(response));
}

export async function paginateDonationsSuperadmin(
  searchUrl: string,
  charityId: string,
  pageNumber: number,
  pageSize: number,
  sortBy: string,
  asc: boolean,
  from: number,
  to: number,
  token: string
): Promise<IPaginatedResponse<IDonation>> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl =
    `${searchUrl}/api/v1/donations/paginated/superadmin?token=${encodeURIComponent(
      resolved
    )}` +
    `${charityId ? "&charityId=" + encodeURIComponent(charityId) : ""}` +
    `&pageNumber=${encodeURIComponent(pageNumber.toString())}` +
    `&pageSize=${encodeURIComponent(pageSize.toString())}` +
    `&sortBy=${encodeURIComponent(sortBy)}` +
    `&ascending=${encodeURIComponent(asc ? "true" : "false")}` +
    `${from ? "&from=" + encodeURIComponent(from.toString()) : ""}` +
    `${to ? "&to=" + encodeURIComponent(to.toString()) : ""}`;
  const time = new Date().getTime();
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(r => {
    const result = r as IPaginatedResponse<IDonation>;
    result.time = time;
    result.result = result.result.map(processDonation);
    return result;
  });
}

function processDonation(donation): IDonation {
  donation.amountPretty = `$ ${moneyFormat(donation.amount / 100.0)}`;
  switch (donation.frequency) {
    case 1:
      donation.frequencyPretty = "One Time";
      break;
    case 2:
      donation.frequencyPretty = "Monthly";
      break;
    case 3:
      donation.frequencyPretty = "Quarterly";
      break;
    case 4:
      donation.frequencyPretty = "Annually";
      break;
    default:
      donation.frequencyPretty = "Unknown";
  }
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };
  donation.receivedPretty = new Date(donation.receivedOn).toLocaleDateString(
    "en-US",
    options
  );
  if (donation.donor) {
    const existingName =
      donation.donor.name != null ? String(donation.donor.name).trim() : "";
    if (!existingName) {
      const first =
        donation.donor.firstName != null
          ? String(donation.donor.firstName).trim()
          : "";
      const last =
        donation.donor.lastName != null
          ? String(donation.donor.lastName).trim()
          : "";
      donation.donor.name = [first, last].filter(Boolean).join(" ");
    }
  }
  if (donation.thankYouLogs) {
    // here we "flatten" the received ThankYouLog objects
    const thankYouLogs = [];
    Object.keys(donation.thankYouLogs).forEach(postId =>
      thankYouLogs.push({
        postId,
        ...donation.thankYouLogs[postId],
      })
    );
    donation.thankYouLogs = thankYouLogs;
  }

  return donation;
}

function processDonor(donor): IUser {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };

  let UnsubscribedFromSms = "";
  if (donor.smsUnsubscribed) {
    UnsubscribedFromSms += donor.smsUnsubscribedOn
      ? new Date(donor.smsUnsubscribedOn).toLocaleDateString("en-US", options)
      : "Not available";
  }

  let UnsubscribedFromEmail = "";
  if (donor.emailUnsubscribed) {
    UnsubscribedFromEmail += donor.emailUnsubscribedOn
      ? new Date(donor.emailUnsubscribedOn).toLocaleDateString("en-US", options)
      : "Not available";
  }
  let keywordsSubscriptionDate = {};

  if (donor.keywordsSubscriptionDate) {
    Object.keys(donor.keywordsSubscriptionDate).forEach(key => {
      keywordsSubscriptionDate = {
        ...keywordsSubscriptionDate,
        [key]: dateFormat(donor.keywordsSubscriptionDate[key], options),
      };
    });
  }
  let createdOn = "";
  if (donor.createdOn) {
    createdOn += new Date(donor.createdOn).toLocaleDateString("en-US", options);
  }

  donor.emailUnsubscribedPretty = UnsubscribedFromEmail;
  donor.smsUnsubscribedPretty = UnsubscribedFromSms;
  donor.keywordsSubscriptionDatesPretty = keywordsSubscriptionDate;
  donor.createdOnPretty = createdOn;
  donor.deactivatedOnPretty =
    donor.deactivatedOn != null
      ? new Date(donor.deactivatedOn).toLocaleDateString("en-US", options)
      : "";
  return donor;
}

export async function bulkDeactivateInvalidContactMeans(
  adminUrl: string,
  charityId: string,
  token: string
): Promise<{ message: string }> {
  const startedAt = Date.now();
  let adminHost = adminUrl;
  try {
    adminHost = new URL(adminUrl).host;
  } catch {
    /* keep raw base string */
  }
  console.info("[Deactivate Invalids] request start", {
    charityId,
    adminHost,
    path: `/api/v1/${charityId}/donors/bulk-deactivate-invalid-contact-means`,
  });
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${adminUrl}/api/v1/${charityId}/donors/bulk-deactivate-invalid-contact-means?token=${encodeURIComponent(
    resolved
  )}`;
  try {
    const res = await customFetchV2({
      url: fullUrl,
      method: "POST",
      mode: "cors",
    });
    console.info("[Deactivate Invalids] request ok", {
      charityId,
      durationMs: Date.now() - startedAt,
      message: res && res.message,
    });
    return res;
  } catch (err) {
    console.error("[Deactivate Invalids] request failed", {
      charityId,
      durationMs: Date.now() - startedAt,
      error: err,
    });
    throw err;
  }
}

export async function executePhoneRevalidation(
  adminUrl: string,
  charityId: string,
  token: string
): Promise<IPhoneRevalidationResult> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${adminUrl}/api/v1/${charityId}/donors/phone-revalidation?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetchV2({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify({ confirm: true }),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function createDonor(
  adminUrl: string,
  charityId: string,
  donor,
  token: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/donor/create?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(donor),
    headers: {
      "content-type": "application/json",
      Accept: "application/json",
    },
  }).then(response => response.message);
}

export async function updateDonor(
  adminUrl: string,
  charityId: string,
  donor,
  token: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/donor/update?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "PUT",
    mode: "cors",
    body: JSON.stringify(donor),
    headers: {
      "content-type": "application/json",
      Accept: "application/json",
    },
  }).then(response => response.message);
}

export async function deleteDonorKeyword(
  adminUrl: string,
  charityId: string,
  ids: Array<string>,
  token: string,
  keywordId: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/keywords/donor/delete?token=${encodeURIComponent(
    resolved
  )}&keywordId=${encodeURIComponent(keywordId)}`;

  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(ids),
    headers: {
      "content-type": "application/json",
      Accept: "application/json",
    },
  }).then(response => response.message);
}

export async function accountNumberExists(
  adminUrl: string,
  charityId: string,
  accountNumber: string,
  token: string
): Promise<boolean> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/donor/account/${accountNumber}?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.success);
}

export async function executeDonationsExportExtended(
  searchUrl: string,
  charityId: string,
  fromDate: number,
  toDate: number,
  targetsId: Array<string>,
  ignoreSelected: boolean,
  token: string
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl =
    `${searchUrl}/api/v1/donations/export/superadmin?token=${encodeURIComponent(
      resolved
    )}` +
    `${charityId ? "&charityId=" + encodeURIComponent(charityId) : ""}` +
    `${fromDate ? `&from=${encodeURIComponent(fromDate.toString())}` : ""}` +
    `${toDate ? `&to=${encodeURIComponent(toDate.toString())}` : ""}` +
    `&ignoreSelected=${encodeURIComponent(ignoreSelected ? "true" : "false")}`;
  const tz = moment.tz.guess();
  if (tz) {
    fullUrl += `&timezone=${encodeURIComponent(tz)}`;
  }
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(targetsId),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function executeDonationsExport(
  searchUrl: string,
  charityId: string,
  fromDate: number,
  toDate: number,
  targetsId: Array<string>,
  ignoreSelected: boolean,
  donorId: string,
  token: string
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl =
    `${searchUrl}/api/v1/donations/export/${charityId}?token=${encodeURIComponent(
      resolved
    )}` +
    `${fromDate ? `&from=${encodeURIComponent(fromDate.toString())}` : ""}` +
    `${toDate ? `&to=${encodeURIComponent(toDate.toString())}` : ""}` +
    `&ignoreSelected=${encodeURIComponent(ignoreSelected ? "true" : "false")}` +
    `${donorId ? `&donorId=${encodeURIComponent(donorId)}` : ""}`;
  const tz = moment.tz.guess();
  if (tz) {
    fullUrl += `&timezone=${encodeURIComponent(tz)}`;
  }
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(targetsId),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function executeDonorsExport(
  searchUrl: string,
  charityId: string,
  targetsId: Array<string>,
  ignoreSelected: boolean,
  token: string,
  filter: string,
  from: number,
  to: number,
  activationFilter: DonorActivationFilterParam = "ACTIVE_ONLY"
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl =
    `${searchUrl}/api/v1/donors/export/${charityId}?token=${encodeURIComponent(
      resolved
    )}&ignoreSelected=${
      ignoreSelected !== undefined ? ignoreSelected : false
    }` +
    (filter ? `&filterBy=${encodeURIComponent(filter)}` : "") +
    `${from ? "&from=" + encodeURIComponent(from.toString()) : ""}` +
    `${to ? "&to=" + encodeURIComponent(to.toString()) : ""}` +
    `&activationFilter=${encodeURIComponent(activationFilter)}`;
  const tz = moment.tz.guess();
  if (tz) {
    fullUrl += `&timezone=${encodeURIComponent(tz)}`;
  }
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(targetsId),
    headers: {
      "content-type": "application/json",
    },
  });
}

/** Parse RFC 6266-style Content-Disposition filename (quoted or unquoted). */
export function parseFilenameFromContentDisposition(
  header: string | null
): string | null {
  if (!header) {
    return null;
  }
  const quoted = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (quoted) {
    try {
      return decodeURIComponent(quoted[1].trim().replace(/^"(.*)"$/, "$1"));
    } catch {
      return quoted[1].trim().replace(/^"(.*)"$/, "$1");
    }
  }
  const ascii = /filename="([^"]+)"/i.exec(header);
  if (ascii) {
    return ascii[1];
  }
  const loose = /filename=([^;\s]+)/i.exec(header);
  if (loose) {
    return loose[1].trim().replace(/^"(.*)"$/, "$1");
  }
  return null;
}

/**
 * Full-charity import-compatible CSV for upload-donor-file (search-service).
 * Uses local calendar date yyyy-MM-dd for filename; prefers Content-Disposition when exposed by CORS.
 */
export async function executeImportCompatibleDonorsExport(
  searchUrl: string,
  charityId: string,
  token: string,
  exportDate: string,
  activationFilter: DonorActivationFilterParam = "ACTIVE_ONLY"
): Promise<{ blob: Blob; filename: string }> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const url = `${searchUrl}/api/v1/donors/export/import-compatible/${charityId}?token=${encodeURIComponent(
    resolved
  )}&exportDate=${encodeURIComponent(
    exportDate
  )}&activationFilter=${encodeURIComponent(activationFilter)}`;

  const response = await staffFetch(url, {
    method: "POST",
    mode: "cors",
    body: undefined,
  });

  if (!response.ok) {
    throw new Error(`Import-compatible export failed (${response.status})`);
  }

  const blob = await response.blob();
  const cd = response.headers.get("Content-Disposition");
  const filename =
    parseFilenameFromContentDisposition(cd) ||
    `donors-import-ready-${exportDate}.csv`;
  return { blob, filename };
}

export async function executeNotificationDetailsExport(
  searchUrl: string,
  charityId: string,
  token: string,
  postId: string
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl = `${searchUrl}/api/v1/export/${charityId}/${postId}?token=${encodeURIComponent(
    resolved
  )}`;
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: null,
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function executeInvalidDonorsExport(
  searchUrl: string,
  charityId: string,
  token: string
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl = `${searchUrl}/api/v1/donors/export/invalids/${charityId}?token=${encodeURIComponent(
    resolved
  )}`;
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: null,
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function executeSuperAdminReportExport(
  searchUrl: string,
  token: string,
  charities: Array<ICharity>,
  from: number,
  to: number
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl = `${searchUrl}/api/v1/export/superadmin/report?token=${encodeURIComponent(
    resolved
  )}&from=${encodeURIComponent(from.toString())}&to=${encodeURIComponent(
    to.toString()
  )}`;
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(charities),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function executeSmsSummaryExport(
  searchUrl: string,
  token: string,
  charities: Array<ICharity>,
  from: number,
  to: number
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl = `${searchUrl}/api/v1/metrics/superadmin/sms/csv?token=${encodeURIComponent(
    resolved
  )}&from=${encodeURIComponent(from.toString())}&to=${encodeURIComponent(
    to.toString()
  )}`;
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(charities),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function executeFeesSummaryExport(
  searchUrl: string,
  token: string,
  charities: Array<ICharity>,
  from: number,
  to: number
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl = `${searchUrl}/api/v1/donations/fees/export/superadmin?token=${encodeURIComponent(
    resolved
  )}&from=${encodeURIComponent(from.toString())}&to=${encodeURIComponent(
    to.toString()
  )}`;
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(charities),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function executeDonorsKeywordExport(
  searchUrl: string,
  charityId: string,
  keywordId: string,
  ids: Array<string>,
  token: string
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/${charityId}/keywords/${keywordId}/csv?token=${encodeURIComponent(
    resolved
  )}`;

  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(ids),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function executeConversationsExport(
  notificationUrl: string,
  charityId: string,
  token: string
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${notificationUrl}/v1/chat/${charityId}/csv?token=${encodeURIComponent(
    resolved
  )}`;

  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function executePostExport(
  adminUrl: string,
  charityId: string,
  postId: string,
  token: string,
  repository: string
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl = `${adminUrl}/api/v1/${charityId}/posts/${postId}/csv?token=${encodeURIComponent(
    resolved
  )}&repository=${repository}`;
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: null,
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function executeSegmentDonorsExport(
  searchUrl: string,
  charityId: string,
  segmentId: string,
  isFlag: boolean,
  token: string,
  ids: Array<string>
): Promise<Blob> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl = `${searchUrl}/api/v1/${charityId}/segments/${segmentId}/csv?token=${encodeURIComponent(
    resolved
  )}&isFlag=${isFlag !== undefined ? isFlag : false}`;
  return customBlobFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(ids),
  });
}

export async function getInvalidDonorsCount(
  searchUrl: string,
  charityId: string,
  token: string
): Promise<ITargetCount> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  let fullUrl = `${searchUrl}/api/v1/donors/invalids/count/${charityId}?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    body: null,
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function getReindexStatus(
  searchUrl: string,
  charityId: string
): Promise<IReindexActiveStatus | null> {
  try {
    const resolved = await resolveFirebaseIdTokenForApiCall(null);
    const response = await staffFetch(
      `${searchUrl}/api/v1/reindex/status/${encodeURIComponent(charityId)}`,
      {
        method: "GET",
        mode: "cors",
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${resolved}`,
        },
      }
    );
    if (!response.ok) {
      return null;
    }
    return response.json();
  } catch {
    return null;
  }
}

export async function enqueueReindexOperation(
  searchUrl: string,
  charityId: string,
  type: "donors" | "segments" | "donations",
  reindexMode: "full" | "incremental" = "full"
): Promise<void> {
  const params = new URLSearchParams({ reindexMode });
  const fullUrl = `${searchUrl}/api/v1/enqueueReindexOperation/${encodeURIComponent(
    charityId
  )}/${encodeURIComponent(type)}?${params.toString()}`;
  const resolved = await resolveFirebaseIdTokenForApiCall(null);
  await customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${resolved}`,
    },
  });
}

export async function updatePhotoUrl(
  authUrl: string,
  token: string,
  data: any
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${authUrl}/v1/admin/updatePhoto?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.message);
}

export async function updateProfile(
  authUrl: string,
  token: string,
  data: any
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${authUrl}/v1/admin/updateProfile?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "PUT",
    mode: "cors",
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.message);
}

export async function resetPassword(
  authUrl: string,
  token: string,
  data: { oldPassword: string; newPassword: string; completionToken?: string }
): Promise<{ message: string; token?: string }> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${authUrl}/v1/admin/reset?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetchV2({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json",
    },
  }).then(response => ({ message: response.message, token: response.token }));
}

export async function setCustomDonationUrl(
  charityId: string,
  adminUrl: string,
  token: string,
  newUrl: string
): Promise<null> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/config/donationUrl?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify({ customDonationUrl: newUrl }),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function setHighlightColor(
  charityId: string,
  adminUrl: string,
  token: string,
  highlightColor: string
): Promise<null> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/config/highlightColor?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify({ highlightColor }),
    headers: {
      "content-type": "application/json",
    },
  });
}

/**
 * Default sending identity for a charity (Redis `emailAccounts` hash), from admin-service
 * GET `/api/v1/{charityId}/email`.
 */
export async function getSendingEmailAccount(
  charityId: string,
  adminUrl: string,
  token: string
): Promise<IEmailAccount> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${adminUrl}/api/v1/${charityId}/email?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then((response: unknown) => {
    const r = response as IEmailAccount;
    if (
      r == null ||
      (String(r.email || "").length === 0 && String(r.name || "").length === 0)
    ) {
      return { name: "", email: "" };
    }
    return r;
  });
}

export async function clonePost(
  charityId: string,
  postType: PostType,
  postId: string,
  adminUrl: string,
  token: string
): Promise<IClonePostResponse> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/post/${postId}/clone?token=${encodeURIComponent(
    resolved
  )}&origin=Web&isPublished=${postType === "published"}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
  }).then(response => response as IClonePostResponse);
}

export async function updateDashboardOtherSetting(
  charityId: string,
  field: string,
  enabled: boolean | null,
  adminUrl: string,
  token: string,
  value?: number,
  stringValue?: string
): Promise<void> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/dashboard/other-settings?token=${encodeURIComponent(
    resolved
  )}`;
  const body =
    typeof value === "number"
      ? { field, value }
      : typeof stringValue === "string"
      ? { field, stringValue }
      : { field, enabled };
  const response = await staffFetch(fullUrl, {
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`update dashboard setting failed: ${response.status}`);
  }
}

export async function deletePost(
  charityId: string,
  postType: PostType,
  postId: string,
  adminUrl: string,
  token: string
): Promise<void> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/post/${postId}?token=${encodeURIComponent(
    resolved
  )}&isPublished=${postType === "published"}`;
  const response = await staffFetch(fullUrl, {
    method: "DELETE",
    mode: "cors",
  });
  if (!response.ok) {
    throw new Error(`delete post failed: ${response.status}`);
  }
}

export async function deleteSegment(
  charityId: string,
  segmentId: string,
  adminUrl: string,
  token: string
): Promise<void> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/segment/${segmentId}?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "DELETE",
    mode: "cors",
  });
}

export async function upsertThankYouAdminGlobal(
  adminUrl: string,
  charityId: string,
  userId: string,
  token: string,
  threshold: number | null,
  firstMonthlyThreshold: number | null,
  maxThreshold: number | null,
  maxFirstMonthlyThreshold: number | null
): Promise<void> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${adminUrl}/api/v1/${charityId}/thank-you-admins/global/${encodeURIComponent(
    userId
  )}?token=${encodeURIComponent(resolved)}`;
  await customFetch({
    url: fullUrl,
    method: "PUT",
    mode: "cors",
    body: JSON.stringify({
      threshold,
      firstMonthlyThreshold,
      maxThreshold,
      maxFirstMonthlyThreshold,
    }),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function removeThankYouAdminGlobal(
  adminUrl: string,
  charityId: string,
  userId: string,
  token: string
): Promise<void> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${adminUrl}/api/v1/${charityId}/thank-you-admins/global/${encodeURIComponent(
    userId
  )}?token=${encodeURIComponent(resolved)}`;
  await customFetch({
    url: fullUrl,
    method: "DELETE",
    mode: "cors",
  });
}

export async function upsertThankYouAdminList(
  adminUrl: string,
  charityId: string,
  segmentId: string,
  userId: string,
  token: string,
  threshold: number | null,
  firstMonthlyThreshold: number | null,
  maxThreshold: number | null,
  maxFirstMonthlyThreshold: number | null
): Promise<void> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${adminUrl}/api/v1/${charityId}/thank-you-admins/segments/${encodeURIComponent(
    segmentId
  )}/${encodeURIComponent(userId)}?token=${encodeURIComponent(resolved)}`;
  await customFetch({
    url: fullUrl,
    method: "PUT",
    mode: "cors",
    body: JSON.stringify({
      threshold,
      firstMonthlyThreshold,
      maxThreshold,
      maxFirstMonthlyThreshold,
    }),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function removeThankYouAdminList(
  adminUrl: string,
  charityId: string,
  segmentId: string,
  userId: string,
  token: string
): Promise<void> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${adminUrl}/api/v1/${charityId}/thank-you-admins/segments/${encodeURIComponent(
    segmentId
  )}/${encodeURIComponent(userId)}?token=${encodeURIComponent(resolved)}`;
  await customFetch({
    url: fullUrl,
    method: "DELETE",
    mode: "cors",
  });
}

export async function bulkUpsertThankYouAdminLists(
  adminUrl: string,
  charityId: string,
  token: string,
  userId: string,
  segmentIds: string[],
  threshold: number | null,
  firstMonthlyThreshold: number | null,
  maxThreshold: number | null,
  maxFirstMonthlyThreshold: number | null
): Promise<{ updatedCount: number; skippedCount: number }> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${adminUrl}/api/v1/${charityId}/thank-you-admins/segments/bulk?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify({
      userId,
      segmentIds,
      threshold,
      firstMonthlyThreshold,
      maxThreshold,
      maxFirstMonthlyThreshold,
    }),
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function messengerAvailability(
  url: string,
  charityID: string,
  token: string
): Promise<IBasicResponse> {
  const requestAvailability = async (
    forceRefresh: boolean
  ): Promise<IBasicResponse> => {
    let resolved: string;
    if (forceRefresh) {
      const refreshed = await ensureAccessToken({ forceRefresh: true });
      if (!isUsableServiceAccessToken(refreshed)) {
        throw new Error("Missing or invalid Firebase ID token");
      }
      resolved = (refreshed as string).trim();
    } else {
      resolved = await resolveFirebaseIdTokenForApiCall(token);
    }

    const fullUrl = `${url}/v1/messenger/${charityID}/available?token=${encodeURIComponent(
      resolved
    )}`;
    const response = await customFetchV2({
      url: fullUrl,
      method: "GET",
      mode: "cors",
      suppressSessionExpiredNotification: true,
    });
    if (
      response &&
      typeof response.message === "string" &&
      response.message.length > 0 &&
      response.success == null
    ) {
      throw new Error(response.message);
    }
    return response as IBasicResponse;
  };

  try {
    return await requestAvailability(false);
  } catch (firstError) {
    try {
      return await requestAvailability(true);
    } catch {
      throw firstError;
    }
  }
}

export async function getChats(
  url: string,
  charityID: string,
  page: number = 0,
  token: string
): Promise<IChatListResponse> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${url}/v1/chat/${charityID}?page=${page}&size=30&sort=date,desc&token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => {
    if (response.message) {
      throw new Error(response.message);
    } else {
      return response;
    }
  });
}

export async function getMessages(
  url: string,
  charityID: string,
  donorId: string,
  token: string
): Promise<IChat> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${url}/v1/chat/${charityID}/donor?token=${encodeURIComponent(
    resolved
  )}&donorId=${encodeURIComponent(donorId)}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => {
    if (response.message) {
      throw new Error(response.message);
    } else {
      return response;
    }
  });
}

export function getSendingPhoneNumber(
  url: string,
  charityID: string,
  token: string
): Promise<any> {
  const fullUrl = `${url}/api/v1/${charityID}/sendingPhoneNumber?token=${encodeURIComponent(
    token
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => {
    return response;
  });
}

export async function sendMessage(
  url: string,
  charityID: string,
  token: string,
  message: ISendMessageRequest
): Promise<ISendMessageResponse> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${url}/v1/chat/${charityID}/send?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(message),
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    if (response.error) {
      throw new Error(response.error);
    } else {
      return response;
    }
  });
}

export async function searchChats(
  url: string,
  charityID: string,
  token: string,
  query: string
): Promise<Array<IChat>> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${url}/v1/chat/${charityID}/lookup?query=${encodeURIComponent(
    query
  )}&token=${encodeURIComponent(resolved)}`;

  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => {
    return response;
  });
}

export async function searchChatsByDate(
  url: string,
  charityID: string,
  token: string,
  query: string,
  page: number = 0
): Promise<IChatListResponse> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${url}/v1/chat/${charityID}/lookup/date?page=${page}&size=30&sort=date,desc&query=${query}&token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => {
    return response;
  });
}

export async function searchChatsByDateRange(
  url: string,
  charityID: string,
  token: string,
  from?: number,
  to?: number,
  page: number = 0
): Promise<IChatListResponse> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fromParam =
    typeof from === "number" ? `&from=${encodeURIComponent(from)}` : "";
  const toParam = typeof to === "number" ? `&to=${encodeURIComponent(to)}` : "";
  const fullUrl = `${url}/v1/chat/${charityID}/lookup/date?page=${page}&size=30&sort=date,desc${fromParam}${toParam}&token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => {
    return response;
  });
}

export const customBlobFetch = async (fetchData: FetchData): Promise<Blob> => {
  const response = await staffFetch(fetchData.url, {
    body: fetchData.body ? fetchData.body : undefined,
    headers: fetchData.headers ? fetchData.headers : undefined,
    method: fetchData.method,
    mode: fetchData.mode,
  });
  if (!response.ok) {
    const text = await response.text();
    notifySessionExpiredFromBody({}, text);
    throw new Error(text || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.blob();
};

export const customFetchV2 = (
  fetchData: FetchData,
  decodeData: boolean = true
): Promise<any> => {
  const makeRequest = (url: string, freshToken?: string) => {
    return staffFetch(url, {
      body: fetchData.body ? fetchData.body : undefined,
      headers: freshToken
        ? { ...fetchData.headers, Authorization: `Bearer ${freshToken}` }
        : fetchData.headers,
      method: fetchData.method,
      mode: fetchData.mode,
    });
  };

  return makeRequest(fetchData.url).then(async response => {
    const messageFromFailed = async (res: Response) => {
      const { body, text } = await readJsonOrEmpty(res);
      if (!fetchData.suppressSessionExpiredNotification) {
        notifySessionExpiredFromBody(body, text);
      }
      if (body && typeof body.message === "string" && body.message.length > 0) {
        return body.message;
      }
      if (body && typeof body.error === "string" && body.error.length > 0) {
        return body.error;
      }
      if (text) {
        return text;
      }
      return `HTTP ${res.status}: ${res.statusText}`;
    };

    if (!response.ok) {
      const failureText = await response.clone().text();
      if (payloadIndicatesSessionExpired(failureText)) {
        throw new Error(await messageFromFailed(response));
      }
      if (
        response.status === 401 &&
        !fetchData.url.includes("/charity/invite/check")
      ) {
        const user = firebase.auth().currentUser;

        if (user) {
          try {
            const freshToken = await user.getIdToken(true);
            const urlWithFreshToken = fetchData.url.replace(
              /([?&])token=([^&]*)/,
              `$1token=${encodeURIComponent(freshToken)}`
            );

            const retryResponse = await makeRequest(
              urlWithFreshToken,
              freshToken
            );

            if (!retryResponse.ok) {
              throw new Error(await messageFromFailed(retryResponse));
            }

            if (!decodeData) return retryResponse;
            return (await readJsonOrEmpty(retryResponse)).body;
          } catch (refreshError) {
            console.error(
              "[customFetchV2] Token refresh failed:",
              refreshError
            );
            throw new Error(`Authentication failed: ${refreshError.message}`);
          }
        }
      }

      throw new Error(await messageFromFailed(response));
    }

    if (!decodeData) return response;
    return (await readJsonOrEmpty(response)).body;
  });
};

export const customFetch = customFetchV2;

export const basicCustomFetch = (fetchData: FetchData) => {
  return staffFetch(fetchData.url, {
    body: fetchData.body ? fetchData.body : undefined,
    headers: fetchData.headers ? fetchData.headers : undefined,
    method: fetchData.method,
    mode: fetchData.mode,
  });
};

export async function deactivateDonor(
  adminUrl: string,
  charityId: string,
  request,
  token: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/donor/deactivate?token=${encodeURIComponent(
    resolved
  )}`;

  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(request),
    headers: {
      "content-type": "application/json",
      Accept: "application/json",
    },
  }).then(response => response.message);
}

export async function queueFileForProcessing(
  adminUrl: string,
  charityId: string,
  fileRequestId: string,
  token: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/upload/${fileRequestId}?token=${encodeURIComponent(
    resolved
  )}`;

  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.message);
}

export async function unsubscribeDonor(
  adminUrl: string,
  charityId: string,
  token: string,
  donorId: string,
  flag: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/donor/unsubscribe?token=${encodeURIComponent(
    resolved
  )}&donorId=${encodeURIComponent(donorId)}&unsubscribe=${flag}`;

  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.message);
}

export async function resubscribeDonor(
  adminUrl: string,
  charityId: string,
  token: string,
  donorId: string,
  flag: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/donor/unsubscribe?token=${encodeURIComponent(
    resolved
  )}&donorId=${encodeURIComponent(donorId)}&unsubscribe=${flag}`;

  return customFetch({
    url: fullUrl,
    method: "DELETE",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => response.message);
}

export async function getValidTime(
  adminUrl: string,
  charityId: string,
  token: string,
  scheduleTime: number
): Promise<IValidtimeResponse> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/validDate?token=${encodeURIComponent(
    resolved
  )}${
    scheduleTime
      ? `&scheduleTime=${encodeURIComponent(scheduleTime.toString())}`
      : ""
  }`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    return response;
  });
}

export async function getCurrentTime(
  adminUrl: string,
  charityId: string,
  token: string
): Promise<IValidtimeResponse> {
  try {
    const resolved = await resolveFirebaseIdTokenForApiCall(token);

    const fullUrl = `${adminUrl}/api/v1/${charityId}/currentDate?token=${encodeURIComponent(
      resolved
    )}`;
    return await customFetch({
      url: fullUrl,
      method: "GET",
      mode: "cors",
      headers: {
        "content-type": "application/json",
      },
    }).then(response => {
      return response;
    });
  } catch (e) {
    console.warn("getCurrentTime: request failed, using client clock", e);
    const now = Date.now();
    return {
      isValid: true,
      isNextDay: false,
      time: now,
      nextPublishTime: now,
    };
  }
}

export async function subjectLength(
  adminUrl: string,
  charityId: string,
  token: string
): Promise<IContentResponse> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/subjectLength?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => response as IContentResponse);
}

export async function titleLenght(
  adminUrl: string,
  charityId: string,
  token: string
): Promise<ITitleLength> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${adminUrl}/api/v1/${charityId}/titleLength?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  }).then(response => response as ITitleLength);
}

export async function getFlagsForDonor(
  searchUrl: string,
  charityId: string,
  donorId: string,
  token: string
): Promise<Array<ISegment>> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/donors/flags/${charityId}?token=${encodeURIComponent(
    resolved
  )}&donorId=${encodeURIComponent(donorId)}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  });
}

export async function setFlagsForDonor(
  searchUrl: string,
  charityId: string,
  data: ISetFlagsForDonorRequest,
  token: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${searchUrl}/api/v1/donors/flags/${charityId}?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json",
    },
    mode: "cors",
  }).then(response => response.message);
}

const SEGMENT_MEMBERS_BATCH_SIZE = 500;

/** Adds or removes list-segment members through search-service. */
export async function updateSegmentMembers(
  searchUrl: string,
  charityId: string,
  segmentId: string,
  add: string[],
  remove: string[],
  token: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${searchUrl}/api/v1/${encodeURIComponent(
    charityId
  )}/segments/${encodeURIComponent(
    segmentId
  )}/members?token=${encodeURIComponent(resolved)}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify({ add, remove }),
    headers: {
      "content-type": "application/json",
      Accept: "application/json",
    },
  }).then(response => response.message);
}

/** Same as {@link updateSegmentMembers}, in batches of 500 ids. */
export async function updateSegmentMembersBatched(
  searchUrl: string,
  charityId: string,
  segmentId: string,
  add: string[],
  remove: string[],
  token: string
): Promise<void> {
  const addIds = (add || []).filter(id => !!id);
  const removeIds = (remove || []).filter(id => !!id);
  for (
    let index = 0;
    index < addIds.length;
    index += SEGMENT_MEMBERS_BATCH_SIZE
  ) {
    await updateSegmentMembers(
      searchUrl,
      charityId,
      segmentId,
      addIds.slice(index, index + SEGMENT_MEMBERS_BATCH_SIZE),
      [],
      token
    );
  }
  for (
    let index = 0;
    index < removeIds.length;
    index += SEGMENT_MEMBERS_BATCH_SIZE
  ) {
    await updateSegmentMembers(
      searchUrl,
      charityId,
      segmentId,
      [],
      removeIds.slice(index, index + SEGMENT_MEMBERS_BATCH_SIZE),
      token
    );
  }
}

export async function connectToDonorPerfect(
  donorPerfectUrl: string,
  charityId: string,
  config: IDonorPerfectConfig,
  token: string
): Promise<IConnectResponse> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${donorPerfectUrl}/api/v1/${charityId}/connect?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    body: JSON.stringify(config),
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function getDonorPerfectCodes(
  donorPerfectUrl: string,
  charityId: string,
  apiKey: string,
  token: string
): Promise<ICodesResponse> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${donorPerfectUrl}/api/v1/${charityId}/codes?apiKey=${apiKey}&token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  });
}

export async function getDonorPerfectConfig(
  donorPerfectUrl: string,
  charityId: string,
  token: string
): Promise<IDonorPerfectConfig> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${donorPerfectUrl}/api/v1/${charityId}/config?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  });
}

export async function getDonorPerfectLastResult(
  donorPerfectUrl: string,
  charityId: string,
  token: string
): Promise<ISyncResult> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${donorPerfectUrl}/api/v1/${charityId}/lastresult?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  });
}

export async function setDonorPerfectSyncing(
  donorPerfectUrl: string,
  charityId: string,
  syncingRequest: ISyncingRequest,
  token: string
): Promise<any> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${donorPerfectUrl}/api/v1/${charityId}/syncing?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    body: JSON.stringify(syncingRequest),
    method: "PUT",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export async function donorPerfectSyncNow(
  donorPerfectUrl: string,
  charityId: string,
  token: string
): Promise<ISyncResult> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${donorPerfectUrl}/api/v1/${charityId}/syncnow?token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export const virtuousGetTagsList = async (
  adminUrl: string,
  charityId: string,
  apiKey: string,
  token: string
): Promise<any> => {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${adminUrl}/api/v1/${charityId}/virtuous/tags?apiToken=${apiKey}&token=${encodeURIComponent(
    resolved
  )}`;
  return customFetch({
    url: fullUrl,
    method: "GET",
    mode: "cors",
  });
};

export const virtuousConnect = async (
  adminUrl: string,
  charityId: string,
  apiKey: string,
  tags: Segment[],
  token: string
): Promise<any> => {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${adminUrl}/api/v1/${charityId}/virtuous/setup?token=${encodeURIComponent(
    resolved
  )}`;
  const body = {
    apiKey: apiKey,
    selectedSegments: tags,
  };
  return customFetch(
    {
      url: fullUrl,
      method: "POST",
      mode: "cors",
      body: JSON.stringify(body),
      headers: {
        "content-type": "application/json",
      },
    },
    false
  );
};

export const virtuousStopSync = async (
  adminUrl: string,
  charityId: string,
  apiKey: string,
  webhookId: number,
  token: string
): Promise<any> => {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${adminUrl}/api/v1/${charityId}/virtuous/stop?token=${encodeURIComponent(
    resolved
  )}`;
  const body = {
    apiKey: apiKey,
    webhookId: webhookId,
  };
  return customFetch({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify(body),
    headers: {
      "content-type": "application/json",
    },
  });
};

export async function subscribeEmailNotification(
  notificationServiceUrl: string,
  charityId: string,
  donorId: string,
  email: string,
  token: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${notificationServiceUrl}/api/v1/email/subscription/subscribe?charityId=${encodeURIComponent(
    charityId
  )}&donorId=${encodeURIComponent(donorId)}&email=${encodeURIComponent(
    email
  )}&token=${encodeURIComponent(resolved)}`;
  return customFetch(
    {
      url: fullUrl,
      method: "POST",
      mode: "cors",
      headers: {
        "content-type": "application/json",
      },
    },
    false
  ).then(response => {
    if (typeof response === "string") {
      return response;
    }
    return response.message || "Successfully subscribed";
  });
}

export async function unsubscribeEmailNotification(
  notificationServiceUrl: string,
  charityId: string,
  donorId: string,
  email: string,
  token: string,
  postId?: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  let fullUrl = `${notificationServiceUrl}/api/v1/email/subscription/unsubscribe?charityId=${encodeURIComponent(
    charityId
  )}&donorId=${encodeURIComponent(donorId)}&email=${encodeURIComponent(
    email
  )}&token=${encodeURIComponent(resolved)}`;

  if (postId) {
    fullUrl += `&postId=${encodeURIComponent(postId)}`;
  }

  return customFetch(
    {
      url: fullUrl,
      method: "POST",
      mode: "cors",
      headers: {
        "content-type": "application/json",
      },
    },
    false
  ).then(response => {
    if (typeof response === "string") {
      return response;
    }
    return response.message || "Successfully unsubscribed";
  });
}

export async function subscribeSmsNotification(
  notificationServiceUrl: string,
  charityId: string,
  donorId: string,
  token: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);

  const fullUrl = `${notificationServiceUrl}/api/v1/sms/subscription/subscribe?charityId=${encodeURIComponent(
    charityId
  )}&donorId=${encodeURIComponent(donorId)}&token=${encodeURIComponent(
    resolved
  )}`;

  return customFetch(
    {
      url: fullUrl,
      method: "POST",
      mode: "cors",
      headers: {
        "content-type": "application/json",
      },
    },
    false
  ).then(response => {
    if (typeof response === "string") {
      return response;
    }
    return response.message || "Successfully subscribed";
  });
}

export async function unsubscribeSmsNotification(
  notificationServiceUrl: string,
  charityId: string,
  donorId: string,
  phoneNumber: string,
  token: string
): Promise<string> {
  const resolved = await resolveFirebaseIdTokenForApiCall(token);
  const fullUrl = `${notificationServiceUrl}/api/v1/sms/subscription/unsubscribe?charityId=${encodeURIComponent(
    charityId
  )}&donorId=${encodeURIComponent(donorId)}&phoneNumber=${encodeURIComponent(
    phoneNumber
  )}&token=${encodeURIComponent(resolved)}`;

  return customFetch(
    {
      url: fullUrl,
      method: "POST",
      mode: "cors",
      headers: {
        "content-type": "application/json",
      },
    },
    false
  ).then(response => {
    if (typeof response === "string") {
      return response;
    }
    return response.message || "Successfully unsubscribed";
  });
}

export interface IDnsVerificationResponse {
  success: boolean;
  message: string;
  verifiedRecords?: string[];
  failedRecords?: string[];
  // New backend response format
  id?: number;
  valid?: boolean;
  validation_results?: {
    [key: string]: {
      valid: boolean;
      reason: string;
    };
  };
}

export interface IDnsEntriesResponse {
  id: number;
  subdomain: string;
  domain: string;
  username: string;
  ips: string[];
  legacy: boolean;
  valid: boolean;
  dns: {
    [key: string]: {
      valid: boolean;
      type: string;
      host: string;
      data: string;
    };
  };
  user_id: number;
  custom_spf: boolean;
  default: boolean;
  automatic_security: boolean;
  last_validation_attempt_at: number;
}

export function getDomain(
  adminUrl: string,
  charityId: string,
  token: string
): Promise<IDnsEntriesResponse> {
  const fullUrl = `${adminUrl}/api/v1/${charityId}/email-domain?token=${encodeURIComponent(
    token
  )}`;

  return customFetchV2({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export function validateDomain(
  adminUrl: string,
  charityId: string,
  token: string
): Promise<IDnsVerificationResponse> {
  const fullUrl = `${adminUrl}/api/v1/${charityId}/email-domain/validate?token=${encodeURIComponent(
    token
  )}`;

  return customFetchV2({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify({}),
    headers: {
      "content-type": "application/json",
    },
  });
}

export function setupDomain(
  adminUrl: string,
  charityId: string,
  domain: string,
  token: string
): Promise<IDnsEntriesResponse> {
  const fullUrl = `${adminUrl}/api/v1/${charityId}/email-setup?domain=${encodeURIComponent(
    domain
  )}&token=${encodeURIComponent(token)}`;

  return customFetchV2({
    url: fullUrl,
    method: "POST",
    mode: "cors",
  }).then(response => {
    return response;
  });
}

export function registerSendingEmails(
  adminUrl: string,
  charityId: string,
  token: string,
  sendingEmails: Array<{
    name: string;
    email: string;
    defaultEmail: boolean;
  }>
): Promise<string> {
  const fullUrl = `${adminUrl}/api/v1/${charityId}/sending-emails?token=${encodeURIComponent(
    token
  )}`;
  return customFetchV2({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify({ sendingEmails }),
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    if (response.error || response.message === "Unauthorized") {
      throw new Error(
        response.error ||
          response.message ||
          "Failed to register sending emails"
      );
    }
    return response;
  });
}

export interface SendingEmail {
  name: string;
  email: string;
  defaultEmail: boolean;
}

export function fetchSendingEmails(
  adminUrl: string,
  charityId: string,
  token: string
): Promise<SendingEmail[]> {
  const fullUrl = `${adminUrl}/api/v1/${charityId}/sending-emails?token=${encodeURIComponent(
    token
  )}`;
  return customFetchV2({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  }).then(response => {
    if (response.error || response.message === "Unauthorized") {
      throw new Error(
        response.error || response.message || "Failed to fetch sending emails"
      );
    }
    return response.sendingEmails || [];
  });
}

export function saveCharityInfo(
  adminUrl: string,
  charityId: string,
  token: string,
  charityInfo: any
): Promise<any> {
  const fullUrl = `${adminUrl}/api/v1/${charityId}/charity-info?token=${encodeURIComponent(
    token
  )}`;
  return customFetchV2(
    {
      url: fullUrl,
      method: "POST",
      mode: "cors",
      body: JSON.stringify(charityInfo),
      headers: {
        "content-type": "application/json",
      },
    },
    false
  );
}

export function fetchCharityInfo(
  adminUrl: string,
  charityId: string,
  token: string
): Promise<any> {
  const fullUrl = `${adminUrl}/api/v1/${charityId}/charity-info?token=${encodeURIComponent(
    token
  )}`;
  return customFetchV2({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export function fetchDonorSiteCustomizationFromYml(
  adminUrl: string,
  charityId: string,
  token: string
): Promise<Record<string, unknown>> {
  const fullUrl = `${adminUrl}/api/v1/customization/donor-site/${charityId}?token=${encodeURIComponent(
    token
  )}`;
  return customFetchV2({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export function customizeDonorSite(
  adminUrl: string,
  charityId: string,
  token: string,
  customizationData: any,
  wlwSubdomain?: string
): Promise<void> {
  let fullUrl = `${adminUrl}/api/v1/customization/donor-site/${charityId}?token=${encodeURIComponent(
    token
  )}`;

  // Add wlwSubdomain to query params if provided
  if (wlwSubdomain) {
    fullUrl += `&wlwSubdomain=${encodeURIComponent(wlwSubdomain)}`;
  }

  return customFetchV2(
    {
      url: fullUrl,
      method: "POST",
      mode: "cors",
      body: JSON.stringify(customizationData),
      headers: {
        "content-type": "application/json",
      },
    },
    false
  );
}

export function fetchBrandAssets(
  adminUrl: string,
  charityId: string,
  domain: string,
  token: string
): Promise<BrandAssetsResponse> {
  const fullUrl = `${adminUrl}/api/v2/brand/fetch/${encodeURIComponent(
    charityId
  )}?domain=${encodeURIComponent(domain)}&token=${encodeURIComponent(token)}`;

  return customFetchV2({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

/**
 * Check if a subdomain is available for use
 */
export async function checkSubdomainAvailability(
  adminServiceUrl: string,
  charityId: string,
  subdomain: string,
  serviceAccessToken: string
): Promise<{ available: boolean; message?: string }> {
  const url = `${adminServiceUrl}/api/v1/customization/check-wlw-domain/${charityId}/${subdomain}?token=${encodeURIComponent(
    serviceAccessToken
  )}`;

  const response = await customFetchV2({
    url,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });

  // Backend returns { available: boolean }
  return {
    available: response.available,
    message: response.available
      ? undefined
      : "This subdomain is already in use",
  };
}

export function getEntriToken(
  adminUrl: string,
  charityId: string,
  token: string
): Promise<{ authToken: string; applicationId: string }> {
  const fullUrl = `${adminUrl}/api/v2/${encodeURIComponent(
    charityId
  )}/entri/token?token=${encodeURIComponent(token)}`;
  return customFetchV2({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

export interface IAreaCodeAvailabilityResponse {
  available: boolean;
  areaCode: string;
  message?: string;
}

export interface IPortabilityCheckResponse {
  portable: boolean;
  phoneNumber: string;
  estimatedDays?: number;
  message?: string;
  portType?: string;
  losingCarrier?: string;
}

/**
 * Check if phone numbers are available for a given area code via Bandwidth API
 */
export function checkAreaCodeAvailability(
  adminUrl: string,
  areaCode: string,
  charityId: string,
  token: string
): Promise<IAreaCodeAvailabilityResponse> {
  const fullUrl = `${adminUrl}/api/v1/${charityId}/mms/check-area-code?areaCode=${encodeURIComponent(
    areaCode
  )}&token=${encodeURIComponent(token)}`;

  return customFetchV2({
    url: fullUrl,
    method: "GET",
    mode: "cors",
    headers: {
      "content-type": "application/json",
    },
  });
}

/**
 * Check if a phone number can be ported to Bandwidth (LNP check)
 */
export function checkPhonePortability(
  adminUrl: string,
  phoneNumber: string,
  charityId: string,
  token: string
): Promise<IPortabilityCheckResponse> {
  const fullUrl = `${adminUrl}/api/v1/${charityId}/mms/check-portability?token=${encodeURIComponent(
    token
  )}`;

  return customFetchV2({
    url: fullUrl,
    method: "POST",
    mode: "cors",
    body: JSON.stringify({ phoneNumber }),
    headers: {
      "content-type": "application/json",
    },
  });
}
